/* eslint-disable camelcase */
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { mergeDeepRight } from 'ramda';

import { AMOUNT_FORMAT_TYPES, AmountFormat } from '@ezugi/constants';
import { HowlOptions } from '@ezugi/howler';
import { GenericPayloadType, GenericStateType, TableChip } from '@ezugi/types';
import { CamelizeObjectProps } from '@ezugi/types/src/utils';

import { configActions } from '../actions/config';
import { FeatureFlags } from '../epics/config/InitialDataResult';
import { LoadTableConfigResponse } from '../epics/config/fetch';

type ConfigSetPayload = GenericPayloadType;
type ConfigUpdatePayload = GenericPayloadType;
type AutoplayConfig = {
  autoplayRounds: number[]
};

type ProgressBarConfig = {
  hideTimer: boolean;
  lastBetsTimer?: number;
  waitForResultsTimer?: number;
};

type BoGeneralConfig = GenericStateType & {
  language: string;
  isTapToPlayVisible: boolean;
  desktopBetToolbarBackground: boolean;
  enableLogo?: boolean;
  availableLanguages: BOLanguage[];
  enableStatisticsBetting?: boolean;
  nameBeforeGreetingMessage?: boolean;
  backgroundMusic?: HowlOptions;
  isEvoPlayer?: boolean;
  allowVideoQualitySwitch?: boolean;
  progressBar: ProgressBarConfig;
  // details about felt configs at https://ezugi1.atlassian.net/wiki/spaces/CLIEN/pages/4999217247/Table+Felt+Configuration
  feltArchYOffset?: number;
  feltArchBorderRadius?: string;
  feltArchBorderRadiusForTopView?: string;
  /** `true` if felt should be updated when camera angle changes */
  feltAdjustPerVideoAngle?: boolean;
  tipsOnResult: boolean;
  betAutoAdjust?: boolean;
  showGameInfoVersion: boolean;
  autoplayReconnectDelay?: number;
  incidentDialogScreenTime?: number;
  gamePayoutsRTP?: string;
  useDynamicVideoDelay?: boolean;
  abrAdaptionRule?: string;
  evoplayerInitialQuality?: string; // evoplayer_initial_quality
  noBackgroundToolbar?: boolean;
  hideGameVideoTint?: boolean;
  showInactivityDialog?: boolean;
  isTutorialVisible?: boolean;
  maxTutorialDisplays?: string;
  noWinBelowBet?: boolean;
  realityCheckButtonsMethod?: string;
  openTableMethod?: string;
  videoScale?: {
    default?: number;
    noMoreBets: number;
  },
  // autoplay is only loaded in the config if the feature flag is also on in the BO
  autoplay?: AutoplayConfig
};

export type FormatConfig = {
  dateFormat?: string;
  kSymbol: string;
  mSymbol: string;
  limitFormat?: AmountFormat;
  betFormat: AmountFormat;
  showLimitCurrency?: boolean;
  // 'decimal' is only for retail games
  // https://ezugi1.atlassian.net/wiki/spaces/RET/pages/4765712638/Retail+Payout+Format
  payoutFormat?: 'multiplier' | 'fraction' | 'decimal' | 'default';
};

export type BOLanguage = {
  // list of supported languages, also see https://ezugi1.atlassian.net/wiki/spaces/GM/pages/3703636548/Supported+Languages
  // 'e3' is 'Sweepstakes'
  code: 'ar' | 'bg' | 'zf' | 'zh' | 'de' | 'en' | 'es' | 'fr' | 'hi' | 'in' | 'it' | 'ja' | 'km' | 'ko'
    | 'lv' | 'lt' | 'hu' | 'ms' | 'nl' | 'pt' | 'ro' | 'ru' | 'tl' | 'th' | 'vi' | 'ta' | 'tr' | 'bn' | 'e3';
  label: string;
};

export type AuthParams = {
  operatorId: string;
  game_id: string;
  table_id: string;
  identifier: string;
  language?: string;
  token?: string;
  postMessage?: string;
  cashierUrl?: string;
};

type BOOSSConfig = {
  access_restricted_codes?: number[];
};

type BOBetsConfig = {
  allowOpposite?: boolean;
};

export type WinnerColorConfig = { amount: number; color: string };

type WinnerListConfig = {
  colorConfig?: Array<WinnerColorConfig>;
  duration?: number;
  roundAmount: boolean;
  showCurrencySymbol: boolean;
  showCurrencyCode: boolean;
};

type FeatureFlagsConfig = CamelizeObjectProps<FeatureFlags>;

type ComplianceConfig = {
  minimal_bet_notification_enabled?: boolean;
  net_position_visible?: boolean;
  start_game_muted?: boolean;
};

type ChatConfig = {
  showPlayerPresence: boolean;
};

export type RetailConfig = {
  isTVSoundMuted?: boolean;
  sequential_round_system?: boolean;
  pyb_background_image?: string;
};

export type CashierConfig = {
  cashierMethod: 'popup' | 'tab';
  cashierPopupWidth?: number;
  cashierPopupHeight?: number;
  cashierUrl?: string;
};

export type GameServerConfig = {
  bet_intent_enabled?: boolean;
};

export type PreloaderConfig = {
  preloaderEnablePowerBy?: string;
  preloaderIconUrl?: string;
  poweredByIconUrl?: string;
  gameLogoUrl?: string;
  poweredBy: string;
  fullScreenImage: boolean;
  preloaderBackgroundUrl?: string;
  showInfoMessage: boolean;
  showLogo: boolean;
  showGameName: boolean;
  showGameLogo: boolean;
};

export type GameGroup = {
  id: number;
  name: string;
  selectGame: string;
  locale: string;
  games: number[];
  tables: number[];
};

type FiltersConfig = {
  tablesSortingType: string;
  gameGroups: GameGroup[];
  gameGroupsIsCustomConfiguration: boolean;
  ezugiRecommendedActive: boolean;
  recommendedTables: number[];
  sortBjCategory: string; // "{"enableSort":true,"groupName":"Blackjack"}"
};

export type WidgetsConfig = GenericStateType & {
  chat: boolean;
  minilobby: boolean;
  liveSupport: boolean;
  fullscreen: boolean;
  cameraAngleChange: boolean;
  cashier: boolean;
};

type BonusConfig = {
  bonusWheelConfig: {
    enabled: boolean;
    tableIds: number[];
    operatorIds: number[];
    currencyThresholds: Record<string, number>;
    campaignId: number;
    tableId: number;
    tableName: string;
    dealerId: number;
    clientDisplaySeconds: number;
    roundSeconds: number;
    multi2XChance: number;
    multi3XChance: number;
    multi5XChance: number;
    starTime: string;
    endTime: string;
    currencyChips: Record<string, TableChip[]>;
    operatorSettings?: Record<string, {startTime: string; endTime: string;}>;
    maxTutorialDisplays?: number;
  };
};

export type BOConfig = {
  payouts?: Record<string, number>;
  general: BoGeneralConfig;
  definitions?: GenericStateType;
  preloader?: PreloaderConfig;
  authParams?: AuthParams;
  oss?: BOOSSConfig;
  format?: FormatConfig;
  bets?: BOBetsConfig;
  winnerList: WinnerListConfig;
  featureFlags?: FeatureFlagsConfig;
  retail?: RetailConfig;
  retailRedirectUrl?: string;
  compliance?: ComplianceConfig;
  widgets: WidgetsConfig;
  cashier?: CashierConfig;
  gameServer?: GameServerConfig;
  outgoingCalls: boolean;
  isEvoWrapped: boolean;
  chat: ChatConfig;
  filters?: Partial<FiltersConfig>;
  gameResults?: GenericStateType;
  bonus?: BonusConfig;
  autoplay?: AutoplayConfig
};

export const DEFAULT_FORMAT_CONFIG: FormatConfig = {
  betFormat: AMOUNT_FORMAT_TYPES.EXTEND_LETTER,
  kSymbol: 'K',
  mSymbol: 'M',
  limitFormat: AMOUNT_FORMAT_TYPES.COMMA,
};

const initialState: BOConfig = {
  general: {
    language: 'en',
    availableLanguages: [],
    isTapToPlayVisible: true,
    tipsOnResult: true,
    desktopBetToolbarBackground: false,
    progressBar: {
      hideTimer: false,
    },
    showGameInfoVersion: true,
  },
  winnerList: {
    roundAmount: false,
    showCurrencySymbol: false,
    showCurrencyCode: true,
  },
  format: DEFAULT_FORMAT_CONFIG,
  retail: {},
  widgets: {
    home: false,
    chat: false,
    liveSupport: false,
    minilobby: false,
    fullscreen: false,
    cameraAngleChange: false,
    cashier: false,
  },
  outgoingCalls: true,
  isEvoWrapped: false,
  chat: {
    showPlayerPresence: false,
  },
};

const configSlice = createSlice({
  name: 'config',
  initialState,
  reducers: {
    request: (
      _state,
      _action: PayloadAction<
        undefined | { config: LoadTableConfigResponse; localeGroups?: string[] }
      >,
    ) => {},
    success() {},
    failure() {},
    set(state, { payload }: PayloadAction<ConfigSetPayload>) {
      return {
        ...state,
        ...payload,
      };
    },
    update(state, { payload }: PayloadAction<ConfigUpdatePayload>) {
      return mergeDeepRight(state, payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      configActions.authParams.set,
      (state, { payload }: PayloadAction<AuthParams>) => ({
        ...state,
        authParams: payload,
      }),
    );
    builder.addCase(
      configActions.definitions.set,
      (state, { payload }: PayloadAction<GenericStateType>) => ({
        ...state,
        definitions: payload,
      }),
    );
    builder.addCase(
      configActions.preloader.set,
      (state, { payload }: PayloadAction<PreloaderConfig>) => ({
        ...state,
        preloader: payload,
      }),
    );
    builder.addCase(
      configActions.skin.set,
      (state, { payload }: PayloadAction<GenericStateType>) => ({
        ...state,
        skin: payload,
      }),
    );
    builder.addCase(
      configActions.retail.set,
      (state, { payload }: PayloadAction<GenericStateType>) => ({
        ...state,
        retail: payload,
      }),
    );
    builder.addCase(
      configActions.format.set,
      (state, { payload }: PayloadAction<FormatConfig>) => ({
        ...state,
        format: payload,
      }),
    );
  },
});

export const configActionsFromSlice = configSlice.actions;

export default configSlice.reducer;
